/**
 * QuickStatusOverlay - Compact status display triggered by center hub click
 * Clean implementation following AuthModal pattern with responsive design
 */
import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { X, Expand, Moon, Sun, Sparkles, RotateCcw, MapPin, Clock, ChevronRight } from 'lucide-react';
import { 
  getCihPhaseColor,
  getLunarCihPhase,
  getSeasonCihPhase,
  getZodiacCihPhase,
  CIH_COSMOLOGY
} from '../utils/styleCard';
import { GlossaryTerm } from './GlossaryTerm';
import { CyKLButton } from '../hooks/useContainerSettings';
import { backdropVariants } from '../utils/animationConfig';
import { useResponsive } from '../hooks/useResponsive';
import { useContainerConstraints, CONTAINER_PRESETS } from '../hooks/useContainerConstraints';

const QuickStatusOverlay = ({ 
  isOpen, 
  onClose, 
  cosmicData,
  location,
  onExpandToFull,
  onNavigate,
}) => {
  // Responsive hooks for device-aware UI
  const { isMobile, isPhone, width: viewportWidth } = useResponsive();
  const constraints = useContainerConstraints({
    type: 'overlay',
    maxHeightPercent: 85,
  });
  
  // Get sabbat info
  const sabbats = cosmicData?.sabbats || [];
  const nextSabbat = useMemo(() => {
    if (!sabbats || sabbats.length === 0) return null;
    const today = new Date();
    const todayDOY = Math.floor((today - new Date(today.getFullYear(), 0, 0)) / (1000 * 60 * 60 * 24));
    const sorted = [...sabbats].sort((a, b) => {
      const aAdj = (a.day_of_year || 0) >= todayDOY ? a.day_of_year : (a.day_of_year || 0) + 365;
      const bAdj = (b.day_of_year || 0) >= todayDOY ? b.day_of_year : (b.day_of_year || 0) + 365;
      return aAdj - bAdj;
    });
    return sorted[0];
  }, [sabbats]);

  // Early return AFTER hooks
  if (!isOpen || !cosmicData) return null;

  // Extract data
  const moonData = cosmicData?.positions?.moon || {};
  const sunData = cosmicData?.positions?.sun || {};
  const season = cosmicData?.season || {};
  const cihData = cosmicData?.cih_intelligence || {};
  const locationData = location || cosmicData?.location || {};
  const timestamp = cosmicData?.timestamp;

  // Colors and phases
  const lunarPhase = moonData?.phase_name || moonData?.phase || 'New Moon';
  const lunarCihPhase = getLunarCihPhase(lunarPhase);
  const lunarColor = getCihPhaseColor(lunarCihPhase);
  const sunSign = sunData?.zodiac_sign || 'Aries';
  const sunSignColor = getCihPhaseColor(getZodiacCihPhase(sunSign));
  const currentSeason = season?.season || 'Spring';
  const seasonColor = getCihPhaseColor(getSeasonCihPhase(currentSeason));
  const dominantPhase = cihData?.dominant_phase || 'ACTIVATE';
  const dominantColor = getCihPhaseColor(dominantPhase);
  const NEUTRAL_MUTED = CIH_COSMOLOGY.neutralColors?.labelMuted || '#8899A6';

  // Calculate lunar age
  const phaseAngle = moonData?.phase_angle || 0;
  const lunarAge = (phaseAngle / 360) * 29.53;

  // Format time/place
  const dt = timestamp ? new Date(timestamp) : new Date();
  const formattedDate = dt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  const formattedTime = dt.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
  const cityName = locationData?.city || 'Unknown';

  // Retrogrades
  const retrogrades = cosmicData?.positions ? 
    Object.entries(cosmicData.positions)
      .filter(([_, data]) => data?.is_retrograde)
      .map(([name]) => name) : [];

  // Navigation handler
  const handleNav = (type, key) => {
    onClose();
    if (onNavigate) onNavigate(type, key);
  };

  return (
    <motion.div
      variants={backdropVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="quick-status-backdrop"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
        className="bg-tod-bg-primary/95 backdrop-blur-xl border-2 rounded-xl"
        style={{ 
          borderColor: `${dominantColor}80`,
          width: isPhone ? 'calc(100vw - 24px)' : 'min(360px, calc(100vw - 32px))',
          maxHeight: constraints.maxHeight,
        }}
        onClick={e => e.stopPropagation()}
        data-testid="quick-status-panel"
      >
        {/* Header */}
        <div className="flex justify-between items-start p-4 pb-3 border-b border-tod-brass/30">
          <div className="flex items-center gap-2">
            <Sparkles size={18} style={{ color: dominantColor }} />
            <div>
              <h2 className="text-base font-bold text-tod-text-primary">Quick Status</h2>
              <div className="flex items-center gap-2 text-[10px]" style={{ color: NEUTRAL_MUTED }}>
                <span className="flex items-center gap-0.5"><Clock size={9} /> {formattedDate} • {formattedTime}</span>
                <span className="flex items-center gap-0.5"><MapPin size={9} /> {cityName}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <CyKLButton containerId="quickStatus" className="opacity-70 hover:opacity-100 scale-90" />
            <button onClick={onExpandToFull} className="p-1.5 hover:bg-tod-brass/20 rounded-lg" title="Expand">
              <Expand size={16} className="text-tod-brass" />
            </button>
            <button onClick={onClose} className="p-1.5 hover:bg-tod-brass/20 rounded-lg" data-testid="quick-status-close">
              <X size={18} className="text-tod-text-secondary" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 space-y-3">
          {/* Dominant Phase */}
          <button
            onClick={() => handleNav('phase', dominantPhase)}
            className="w-full p-3 rounded-lg text-left hover:opacity-90 transition-opacity"
            style={{ backgroundColor: `${dominantColor}15`, borderLeft: `4px solid ${dominantColor}` }}
          >
            <div className="text-[10px] uppercase tracking-wider" style={{ color: NEUTRAL_MUTED }}>Dominant Phase</div>
            <div className="text-lg font-bold flex items-center justify-between" style={{ color: dominantColor }}>
              <GlossaryTerm term={dominantPhase.toLowerCase()} variant="text">{dominantPhase}</GlossaryTerm>
              <ChevronRight size={16} />
            </div>
          </button>

          {/* Lunar Phase */}
          <div className="p-3 rounded-lg bg-tod-bg-secondary">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Moon size={14} style={{ color: lunarColor }} />
                <span className="text-xs font-medium" style={{ color: lunarColor }}>{lunarPhase}</span>
              </div>
              <span className="text-[10px]" style={{ color: NEUTRAL_MUTED }}>Day {lunarAge.toFixed(1)}</span>
            </div>
            <div className="h-1.5 bg-tod-bg-tertiary rounded-full overflow-hidden">
              <div className="h-full rounded-full transition-all" style={{ width: `${(lunarAge / 29.53) * 100}%`, backgroundColor: lunarColor }} />
            </div>
          </div>

          {/* Sun Sign */}
          <button
            onClick={() => handleNav('planet', 'sun')}
            className="w-full p-3 rounded-lg bg-tod-bg-secondary text-left hover:opacity-90 flex items-center justify-between"
          >
            <div className="flex items-center gap-2">
              <Sun size={14} style={{ color: sunSignColor }} />
              <span className="text-xs font-medium text-tod-text-primary">Sun in {sunSign}</span>
            </div>
            <ChevronRight size={14} style={{ color: NEUTRAL_MUTED }} />
          </button>

          {/* Season & Sabbat */}
          <div className="grid grid-cols-2 gap-2">
            <div className="p-2.5 rounded-lg bg-tod-bg-secondary">
              <div className="text-[10px] uppercase" style={{ color: NEUTRAL_MUTED }}>Season</div>
              <div className="text-xs font-medium" style={{ color: seasonColor }}>{currentSeason}</div>
            </div>
            {nextSabbat && (
              <div className="p-2.5 rounded-lg bg-tod-bg-secondary">
                <div className="text-[10px] uppercase" style={{ color: NEUTRAL_MUTED }}>Next Sabbat</div>
                <div className="text-xs font-medium text-tod-brass">{nextSabbat.name}</div>
              </div>
            )}
          </div>

          {/* Retrogrades */}
          {retrogrades.length > 0 && (
            <div className="p-3 rounded-lg bg-tod-bg-secondary">
              <div className="text-[10px] uppercase tracking-wider mb-2 flex items-center gap-1" style={{ color: NEUTRAL_MUTED }}>
                <RotateCcw size={10} className="text-tod-error" />
                <GlossaryTerm term="retrograde" variant="text">Retrograde</GlossaryTerm>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {retrogrades.map(name => (
                  <button
                    key={name}
                    onClick={() => handleNav('planet', name)}
                    className="px-2 py-1 text-[10px] rounded bg-tod-error/10 hover:bg-tod-error/20 text-tod-error"
                  >
                    {name.charAt(0).toUpperCase() + name.slice(1)} ℞
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-3 pt-2 border-t border-tod-brass/20 text-center">
          <button
            onClick={onExpandToFull}
            className="text-[11px] flex items-center justify-center gap-1 mx-auto hover:underline"
            style={{ color: dominantColor }}
          >
            View full Contextual Orientation <Expand size={11} />
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default QuickStatusOverlay;
